import { useRef, useState, useMemo } from "react";
import { api, getErrorMessage, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Paperclip, Trash2, Plus, FileDown, Loader2, ArrowUpDown, ChevronDown, ChevronRight } from "lucide-react";

const MONTHS_FR = ["Janv.", "Févr.", "Mars", "Avr.", "Mai", "Juin", "Juil.", "Août", "Sept.", "Oct.", "Nov.", "Déc."];

const relativeTime = (isoDate) => {
  if (!isoDate) return "";
  const target = new Date(isoDate);
  const now = new Date();
  const diff = Math.round((now - target) / (1000 * 60 * 60 * 24));
  if (diff === 0) return "Aujourd'hui";
  if (diff === 1) return "Hier";
  if (diff < 0) return `dans ${-diff} j`;
  if (diff < 30) return `il y a ${diff} j`;
  if (diff < 365) return `il y a ${Math.round(diff / 30)} mois`;
  return `il y a ${Math.round(diff / 365)} an(s)`;
};

const monthKey = (isoDate) => {
  if (!isoDate) return "";
  const d = new Date(isoDate);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const monthLabel = (key) => {
  const [y, m] = key.split("-");
  return `${MONTHS_FR[Number(m) - 1]} ${y}`;
};

/**
 * Historique des échanges du litige avec upload de pièces jointes (GridFS).
 * Affiché sous forme de timeline verticale dense, groupée par mois, avec
 * couleurs distinctes selon l'origine (Nous / Le tiers).
 */
export const DisputeHistoryList = ({ disputeId, entries = [], onChanged, dispute = null, users = [], suppliers = [] }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [origin, setOrigin] = useState("us");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [posting, setPosting] = useState(false);
  const [sortDesc, setSortDesc] = useState(true);
  const [collapsedMonths, setCollapsedMonths] = useState({});
  // Acteur : qui parle dans cet échange ?
  const [actorKey, setActorKey] = useState("");  // "internal_owner" | "supplier:<id>" | "thirdparty:<id>"
  const fileInput = useRef(null);

  // Options d'acteur selon l'origine
  const actorOptions = useMemo(() => {
    if (!dispute) return [];
    if (origin === "us") {
      const opts = [];
      if (dispute.internal_owner_id) {
        const u = users.find((x) => x.id === dispute.internal_owner_id);
        opts.push({ key: "internal_owner", label: `Responsable interne — ${u?.name || u?.email || "inconnu"}` });
      }
      (dispute.external_suppliers || []).forEach((es, idx) => {
        const sup = suppliers.find((s) => s.id === es.supplier_id);
        if (sup) {
          opts.push({
            key: `supplier:${es.supplier_id}`,
            label: `${sup.company_name || sup.name} ${es.role ? `(${es.role})` : ""}`.trim(),
          });
        }
      });
      return opts;
    }
    if (origin === "third_party") {
      const opts = [];
      (dispute.external_thirdparties || []).forEach((tp) => {
        const label = `${tp.name || tp.company || "Tiers"} ${tp.role ? `(${tp.role})` : ""}`.trim();
        opts.push({ key: `thirdparty:${tp.id}`, label });
      });
      return opts;
    }
    return [];
  }, [dispute, origin, users, suppliers]);

  // Reset actor when origin changes
  const handleOriginChange = (v) => {
    setOrigin(v);
    setActorKey("");
  };

  const sortedAndGrouped = useMemo(() => {
    const sorted = [...entries].sort((a, b) => {
      const da = a.date || "";
      const db = b.date || "";
      return sortDesc ? db.localeCompare(da) : da.localeCompare(db);
    });
    const groups = [];
    let currentKey = null;
    sorted.forEach((e) => {
      const k = monthKey(e.date);
      if (k !== currentKey) {
        groups.push({ key: k, label: monthLabel(k), items: [] });
        currentKey = k;
      }
      groups[groups.length - 1].items.push(e);
    });
    return groups;
  }, [entries, sortDesc]);

  const toggleMonth = (key) => setCollapsedMonths((c) => ({ ...c, [key]: !c[key] }));

  const handleSubmit = async () => {
    if (!description.trim() && (!files || files.length === 0)) {
      toast.error("Description ou pièce jointe requise");
      return;
    }
    const fd = new FormData();
    fd.append("date", date);
    fd.append("origin", origin);
    fd.append("description", description);
    // Acteur : décompose actorKey en (actor_type, actor_id)
    if (actorKey) {
      if (actorKey === "internal_owner") {
        fd.append("actor_type", "internal_owner");
        fd.append("actor_id", dispute?.internal_owner_id || "");
      } else if (actorKey.startsWith("supplier:")) {
        fd.append("actor_type", "external_supplier");
        fd.append("actor_id", actorKey.split(":")[1]);
      } else if (actorKey.startsWith("thirdparty:")) {
        fd.append("actor_type", "external_thirdparty");
        fd.append("actor_id", actorKey.split(":")[1]);
      }
    }
    Array.from(files || []).forEach((f) => fd.append("files", f));
    try {
      setPosting(true);
      await api.addDisputeHistory(disputeId, fd);
      toast.success("Entrée ajoutée");
      setDescription("");
      setFiles([]);
      setActorKey("");
      if (fileInput.current) fileInput.current.value = "";
      onChanged?.();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur"));
    } finally {
      setPosting(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm("Supprimer cette entrée d'historique ?")) return;
    try {
      await api.deleteDisputeHistory(disputeId, entryId);
      toast.success("Entrée supprimée");
      onChanged?.();
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur"));
    }
  };

  return (
    <div className="space-y-3" data-testid="dispute-history">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-base flex items-center gap-2">
          Historique des échanges
          <Badge variant="outline" className="text-[10px]">{entries.length}</Badge>
        </h3>
        {entries.length > 1 && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs gap-1"
            onClick={() => setSortDesc((v) => !v)}
            data-testid="history-sort-toggle"
          >
            <ArrowUpDown className="h-3 w-3" />
            {sortDesc ? "Plus récent en haut" : "Plus ancien en haut"}
          </Button>
        )}
      </div>

      {/* Compose form */}
      <div className="border border-zinc-200 rounded-xl p-3 bg-zinc-50/60 space-y-2">
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label className="text-xs">Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="history-date" />
          </div>
          <div>
            <Label className="text-xs">Origine</Label>
            <Select value={origin} onValueChange={handleOriginChange}>
              <SelectTrigger data-testid="history-origin"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="us">Nous</SelectItem>
                <SelectItem value="third_party">Le tiers</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Pièces jointes</Label>
            <input
              ref={fileInput}
              type="file"
              multiple
              onChange={(e) => setFiles(e.target.files)}
              className="block w-full text-xs text-muted-foreground file:mr-2 file:py-1 file:px-2 file:rounded file:border file:bg-background"
              data-testid="history-files"
            />
          </div>
        </div>
        {actorOptions.length > 0 && (
          <div>
            <Label className="text-xs">
              Acteur {origin === "us" ? "(Nous)" : "(Tiers)"}{" "}
              <span className="text-muted-foreground italic">— optionnel</span>
            </Label>
            <Select value={actorKey || "_none"} onValueChange={(v) => setActorKey(v === "_none" ? "" : v)}>
              <SelectTrigger data-testid="history-actor"><SelectValue placeholder="Sélectionner l'acteur" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">— Non spécifié —</SelectItem>
                {actorOptions.map((o) => (
                  <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <Textarea
          rows={2}
          placeholder="Description de l'action / échange..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          data-testid="history-description"
        />
        <div className="flex justify-end">
          <Button onClick={handleSubmit} disabled={posting} data-testid="history-submit" size="sm">
            {posting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
            Ajouter à l&apos;historique
          </Button>
        </div>
      </div>

      {/* Timeline */}
      {entries.length === 0 ? (
        <p className="text-sm text-muted-foreground italic" data-testid="history-list">Aucun échange enregistré pour le moment.</p>
      ) : (
        <div className="relative pl-1" data-testid="history-list">
          {sortedAndGrouped.map((group) => {
            const collapsed = collapsedMonths[group.key];
            return (
              <div key={group.key} className="mb-2">
                <button
                  type="button"
                  onClick={() => toggleMonth(group.key)}
                  className="sticky top-0 z-10 flex items-center gap-1.5 w-full px-2 py-1.5 bg-white text-[11px] font-semibold uppercase tracking-wider text-zinc-500 hover:text-zinc-900 transition-colors rounded-md"
                  data-testid={`history-month-${group.key}`}
                >
                  {collapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                  <span>{group.label}</span>
                  <span className="text-zinc-400 normal-case font-normal">· {group.items.length}</span>
                </button>
                {!collapsed && (
                  <ol className="relative ml-3 mt-1 border-l-2 border-zinc-100">
                    {group.items.map((e) => {
                      const isUs = e.origin === "us";
                      return (
                        <li
                          key={e.id}
                          className="relative pl-5 pr-2 py-2 group hover:bg-zinc-50/60 rounded-r-md transition-colors"
                          data-testid={`history-entry-${e.id}`}
                        >
                          {/* Dot */}
                          <span
                            className={`absolute -left-[7px] top-3 h-3 w-3 rounded-full border-2 border-white shadow-sm ${
                              isUs ? "bg-blue-500" : "bg-purple-500"
                            }`}
                            aria-hidden
                          />
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <span className="font-medium text-sm tabular-nums">{formatDate(e.date)}</span>
                            <span className="text-[11px] text-zinc-400" title={e.date}>{relativeTime(e.date)}</span>
                            <Badge
                              variant="outline"
                              className={`text-[10px] py-0 px-1.5 ${
                                isUs
                                  ? "bg-blue-50 border-blue-200 text-blue-800"
                                  : "bg-purple-50 border-purple-200 text-purple-800"
                              }`}
                            >
                              {isUs ? "Nous" : "Le tiers"}
                            </Badge>
                            {e.actor_type && (() => {
                              let actorLabel = null;
                              if (e.actor_type === "internal_owner") {
                                const u = users.find((x) => x.id === e.actor_id);
                                actorLabel = u?.name || u?.email || "Responsable interne";
                              } else if (e.actor_type === "external_supplier") {
                                const s = suppliers.find((x) => x.id === e.actor_id);
                                const es = (dispute?.external_suppliers || []).find((x) => x.supplier_id === e.actor_id);
                                actorLabel = `${s?.company_name || s?.name || "Fournisseur"}${es?.role ? ` (${es.role})` : ""}`;
                              } else if (e.actor_type === "external_thirdparty") {
                                const tp = (dispute?.external_thirdparties || []).find((x) => x.id === e.actor_id);
                                actorLabel = tp ? `${tp.name || tp.company || "Tiers"}${tp.role ? ` (${tp.role})` : ""}` : null;
                              }
                              return actorLabel ? (
                                <span className="text-[10px] text-zinc-600 italic">· {actorLabel}</span>
                              ) : null;
                            })()}
                            <div className="flex-1" />
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => handleDelete(e.id)}
                              data-testid={`history-delete-${e.id}`}
                              title="Supprimer"
                            >
                              <Trash2 className="h-3 w-3 text-red-600" />
                            </Button>
                          </div>
                          {e.description && (
                            <p className="whitespace-pre-wrap text-sm text-foreground mt-0.5 leading-snug">
                              {e.description}
                            </p>
                          )}
                          {(e.attachments || []).length > 0 && (
                            <div className="mt-1 flex flex-wrap gap-1">
                              {e.attachments.map((att) => (
                                <a
                                  key={att.file_id}
                                  href={api.getDisputeAttachmentUrl(att.file_id)}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1 text-[11px] bg-zinc-100 hover:bg-zinc-200 px-1.5 py-0.5 rounded border border-zinc-200 transition-colors"
                                  data-testid={`history-attach-${att.file_id}`}
                                >
                                  <Paperclip className="h-2.5 w-2.5" />
                                  <span className="max-w-[180px] truncate">{att.filename}</span>
                                  <FileDown className="h-2.5 w-2.5 ml-0.5" />
                                </a>
                              ))}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DisputeHistoryList;
